import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth, redirectByRole } from "../lib/auth";
import { PromoBanner } from "./PublicBanners";

export default function Navbar({ promoLocation = "global" }) {
  const { user, logout, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide font-semibold transition-colors ${
      isActive ? "text-white" : "text-white/70 hover:text-white"
    }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled
          ? "bg-[#090E33]/90 backdrop-blur-md border-b border-white/10"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      {/* Phase 11 fix — render PromoBanner INSIDE the fixed header so the
          banner and nav row share one stacking context, eliminating the
          pointer-event interception that broke the dismiss button. */}
      <PromoBanner location={promoLocation} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group min-w-0" data-testid="nav-logo-link">
          <img
            src="/brand/freddy-symbol.webp"
            alt="Freddy"
            className="h-9 w-auto flex-shrink-0"
          />
          {/* Brand wordmark — always visible, tightened so it fits on mobile too. */}
          <span className="text-white font-black tracking-[0.14em] text-[10px] sm:text-xs lg:text-sm whitespace-nowrap">
            FREDDY COMPUTE ACADEMY
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/courses" className={linkClass} data-testid="nav-courses-link">
            COURSES
          </NavLink>
          <NavLink to="/pricing" className={linkClass} data-testid="nav-pricing-link">
            PRICING
          </NavLink>
          <span
            className="text-sm tracking-wide font-semibold text-white/30 cursor-not-allowed select-none"
            title="Community — coming soon"
            data-testid="nav-community-disabled"
          >
            COMMUNITY · SOON
          </span>
          {loading ? null : user ? (
            <div className="flex items-center gap-4">
              <Link
                to={redirectByRole(user.role)}
                className="text-sm font-semibold text-white/80 hover:text-white"
                data-testid="nav-dashboard-link"
              >
                {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="text-sm font-semibold text-white/60 hover:text-white"
                data-testid="nav-logout-btn"
              >
                LOGOUT
              </button>
              <Link to="/courses" className="btn-fc-red" data-testid="nav-explore-cta">
                EXPLORE
              </Link>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-sm font-semibold text-white/80 hover:text-white" data-testid="nav-login-link">
                LOGIN
              </Link>
              <Link to="/register" className="btn-fc-red" data-testid="nav-get-started-cta">
                GET STARTED
              </Link>
            </div>
          )}
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#060A24] border-t border-white/10" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            <Link to="/courses" onClick={() => setOpen(false)} className="text-white font-semibold">
              Courses
            </Link>
            <Link to="/pricing" onClick={() => setOpen(false)} className="text-white font-semibold">
              Pricing
            </Link>
            <span className="text-white/30 font-semibold">Community · Coming soon</span>
            {user ? (
              <>
                <Link
                  to={redirectByRole(user.role)}
                  onClick={() => setOpen(false)}
                  className="text-white font-semibold"
                >
                  Dashboard
                </Link>
                <button
                  onClick={async () => {
                    await logout();
                    setOpen(false);
                    navigate("/");
                  }}
                  className="text-left text-white/70 font-semibold"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-white font-semibold">
                  Login
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-fc-red text-center">
                  GET STARTED
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
